/*
 * Player identity colors for the tournament comparison view.
 *
 * Each player gets one stable color (assigned by roster order, so a given
 * slot keeps its color regardless of selection order).
 *
 * Two colors per player:
 *  - `solid` / `soft`: the identity tone for UI chrome (selection ring,
 *    comparison bars, legend dots). Vivid and well-separated by hue so the
 *    players can be told apart at a glance.
 *  - `heat`: a VIVID multi-stop gradient for the movement heatmap. It's the
 *    same lush filled heatmap render as the single-player rainbow, just hue-
 *    shifted into a distinct band per player so several maps can sit on the
 *    court at once and still be told apart.
 *
 * Shot markers stay on the outcome palette (bounce / interception / error),
 * but when several entities share the court each hit is leaned subtly toward
 * its owner's `solid` color so the pairs/teams can be told apart (see
 * SmartCourt's IDENTITY_TINT).
 */

/** heatmap.js gradient: normalized value (0..1) → CSS color. */
export type HeatGradient = Record<string, string>;

export interface PlayerColor {
  key: string;
  /** Muted identity color — selection ring, comparison bars, legend dots. */
  solid: string;
  /** Translucent fill of the muted identity color. */
  soft: string;
  /** Vivid hue-band heatmap gradient (low density → bright dense core). */
  heat: HeatGradient;
}

// Deep, well-separated identity hues — red / amber / green / purple — so the
// players read as distinct at a glance while sitting calmly within the report's
// muted slate/navy scheme (the brighter tones contrasted too hard). Each carries
// a same-family heat band for the movement heatmap so a player's chrome color and
// their map agree. Assigned by roster order, so slot 0 is red, slot 1 amber, etc.
const PALETTE: PlayerColor[] = [
  {
    key: 'red',
    solid: '#C42E3C',
    soft: 'rgba(196, 46, 60, 0.16)',
    heat: {
      '0.0': '#7f1d1d',
      '0.4': '#dc2626',
      '0.7': '#ef4444',
      '1.0': '#fca5a5',
    },
  },
  {
    key: 'amber',
    solid: '#C9820F',
    soft: 'rgba(201, 130, 15, 0.16)',
    heat: {
      '0.0': '#92400e',
      '0.4': '#d97706',
      '0.7': '#f59e0b',
      '1.0': '#fde68a',
    },
  },
  {
    key: 'green',
    solid: '#239D42',
    soft: 'rgba(35, 157, 66, 0.16)',
    heat: {
      '0.0': '#166534',
      '0.4': '#16a34a',
      '0.7': '#22c55e',
      '1.0': '#bbf7d0',
    },
  },
  {
    key: 'purple',
    solid: '#7B45BE',
    soft: 'rgba(123, 69, 190, 0.16)',
    heat: {
      '0.0': '#5b21b6',
      '0.4': '#7c3aed',
      '0.7': '#a855f7',
      '1.0': '#ddd6fe',
    },
  },
];

/**
 * The upstream rainbow ramp — used when a single player is selected, keeping
 * the familiar dégradé the single-player view is loved for.
 */
export const RAINBOW_GRADIENT: HeatGradient = {
  '0.0': 'rgb(0, 0, 255)',
  '0.25': 'rgb(0, 255, 255)',
  '0.45': 'rgb(0, 255, 0)',
  '0.65': 'rgb(255, 255, 0)',
  '0.85': 'rgb(255, 136, 0)',
  '1.0': 'rgb(255, 0, 0)',
};

/**
 * Assign a stable color to each player, keyed by their position in the
 * roster order passed in. Caller is responsible for a deterministic order
 * (e.g. left team first, ordered by role).
 */
export function assignPlayerColors(
  orderedIds: string[],
): Record<string, PlayerColor> {
  const out: Record<string, PlayerColor> = {};
  orderedIds.forEach((id, i) => {
    out[id] = PALETTE[i % PALETTE.length];
  });
  return out;
}
